import { computed, type MaybeRefOrGetter, toValue, watch } from 'vue'
import { useQuery, useQueryCache, type QueryCache } from '@pinia/colada'
import { getCustomerAssociations, type CustomerAssociationRow } from '@/api/lytAccountingClient'
import { useAuthStore } from '@/stores/authStore'

/** Association status filter accepted by the customers associations list API. */
export type CustomerAssociationStatusFilter = 'all' | 'associated' | 'unassociated' | 'syncing'

export type CustomerAssociationsQueryParams = {
  page: MaybeRefOrGetter<number>
  pageSize: MaybeRefOrGetter<number>
  status: MaybeRefOrGetter<CustomerAssociationStatusFilter>
}

/**
 * Stable cache key for customer association list reads.
 * Includes entity domain, authenticated integration/store identity when present,
 * pagination, and status — never API keys or other secrets.
 */
export function customerAssociationsQueryKey(parts: {
  integrationId: number | null
  storeId: string | null
  page: number
  pageSize: number
  status: CustomerAssociationStatusFilter
}): readonly [
  'customer-associations',
  number | null,
  string | null,
  number,
  number,
  CustomerAssociationStatusFilter,
] {
  return [
    'customer-associations',
    parts.integrationId,
    parts.storeId,
    parts.page,
    parts.pageSize,
    parts.status,
  ] as const
}

/**
 * Invalidate every customer-associations query entry (all pages/filters).
 * Used after successful link or single-sync so the active list refetches.
 */
export function invalidateCustomerAssociationsQueries(
  queryCache: QueryCache = useQueryCache(),
): Promise<unknown> {
  return queryCache.invalidateQueries({ key: ['customer-associations'] })
}

/**
 * Pinia Colada read boundary for Lytning customer association lists.
 * Pages own filter/page refs; this composable owns the HTTP read + cache key.
 */
export function useCustomerAssociationsQuery(params: CustomerAssociationsQueryParams) {
  const auth = useAuthStore()

  const query = useQuery({
    key: () =>
      customerAssociationsQueryKey({
        integrationId: auth.currentIntegration?.integrationId ?? null,
        storeId: auth.selectedStore?.storeId ?? (auth.storeID || null),
        page: toValue(params.page),
        pageSize: toValue(params.pageSize),
        status: toValue(params.status),
      }),
    query: () => {
      const status = toValue(params.status)
      return getCustomerAssociations({
        page: toValue(params.page),
        pageSize: toValue(params.pageSize),
        status: status === 'all' ? undefined : status,
      })
    },
  })

  const rows = computed<CustomerAssociationRow[]>(() => query.data.value?.customers ?? [])
  const total = computed(() => query.data.value?.total ?? 0)
  const errorMessage = computed(() => {
    const err = query.error.value
    if (!err) return null
    return err instanceof Error ? err.message : String(err)
  })

  // Stamp last successful fetch time without mutating query cache.
  const lastSuccessAt = { current: null as Date | null }
  watch(
    () => query.status.value,
    (status) => {
      if (status === 'success') {
        lastSuccessAt.current = new Date()
      }
    },
  )

  return {
    rows,
    total,
    isLoading: query.isLoading,
    error: query.error,
    errorMessage,
    status: query.status,
    refetch: query.refetch,
    refresh: query.refresh,
    /** Timestamp of the last successful association list fetch in this scope. */
    get lastRefreshAt() {
      return lastSuccessAt.current
    },
    lastRefreshAtRef: computed(() => lastSuccessAt.current),
  }
}
